import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { BannerMessageV2 } from 'ui-components/components/banner-message/banner-message-v2';
import { BannerStatus } from 'ui-components/components/banner-message/banner-enums';
import { CheckboxSlider } from 'ui-components/components/checkbox-slider';
import { FlexColumn, FlexRow, Gap } from 'ui-components/components/flex';
import { NumberInput } from 'ui-components/components/number-input';
import { Text, TextType } from 'ui-components/components/text';
import { GuidService } from 'ui-components/services/guid-service';

import { messages } from './messages';
import styles from './styles.module.less';
import { BLOCKING_BEHAVIOR } from '../../../../../../constants/fraud-constants';
import { FUZZY_THRESHOLD } from '../../constants';
import { LossPreventionCard } from '../../loss-prevention-card';

type AddressBlacklistCardProps = {
  isCardDisabled: boolean;
  enableFuzzyMatching: boolean;
  fuzzyFlagThreshold: number;
  fuzzyBlockThreshold: number;
  onFeatureToggle: (enabled: boolean) => void;
  onSettingChange: (field: string, value: boolean | number | string) => void;
  onManageBlacklist?: () => void;
};

export const AddressBlacklistCard = ({
  isCardDisabled,
  enableFuzzyMatching,
  fuzzyFlagThreshold,
  fuzzyBlockThreshold,
  onFeatureToggle,
  onSettingChange,
  onManageBlacklist,
}: AddressBlacklistCardProps) => {
  const intl = useIntl();
  const fuzzyMatchingId = useMemo(() => GuidService.newGuid(), []);
  const hasThresholdError = enableFuzzyMatching && fuzzyBlockThreshold - fuzzyFlagThreshold < FUZZY_THRESHOLD.MIN_GAP;

  const handleThresholdChange = (field: string) => (value: number | undefined) => {
    if (value !== undefined) {
      onSettingChange(field, value);
    }
  };

  const settings = (
    <FlexColumn gap={Gap.Space16}>
      <FlexColumn gap={Gap.Space8}>
        <FlexRow gap={Gap.Space12}>
          <CheckboxSlider
            checkboxId={fuzzyMatchingId}
            checked={enableFuzzyMatching}
            disabled={isCardDisabled}
            onChange={(enabled) => onSettingChange('enableFuzzyMatching', enabled)}
          />
          <Text textType={TextType.Label} value={messages.fuzzyMatchingLabel} />
        </FlexRow>
        <Text className={styles['help-text']} textType={TextType.Description} value={messages.fuzzyMatchingHelp} />
      </FlexColumn>
      {enableFuzzyMatching ? (
        <FlexColumn gap={Gap.Space12}>
          <FlexRow className={styles['threshold-row']} gap={Gap.Space8}>
            <Text textType={TextType.Label} value={messages.fuzzyFlagThresholdLabel} />
            <NumberInput
              className={styles['threshold-input']}
              disabled={isCardDisabled}
              max={FUZZY_THRESHOLD.FLAG_MAX}
              min={FUZZY_THRESHOLD.FLAG_MIN}
              onChange={handleThresholdChange('fuzzyFlagThreshold')}
              value={fuzzyFlagThreshold}
            />
            <Text textType={TextType.Label} value={messages.matchOrHigher} />
          </FlexRow>
          <FlexRow className={styles['threshold-row']} gap={Gap.Space8}>
            <Text textType={TextType.Label} value={messages.fuzzyBlockThresholdLabel} />
            <NumberInput
              className={styles['threshold-input']}
              disabled={isCardDisabled}
              max={FUZZY_THRESHOLD.BLOCK_MAX}
              min={FUZZY_THRESHOLD.BLOCK_MIN}
              onChange={handleThresholdChange('fuzzyBlockThreshold')}
              value={fuzzyBlockThreshold}
            />
            <Text textType={TextType.Label} value={messages.matchOrHigher} />
          </FlexRow>
          {hasThresholdError ? (
            <BannerMessageV2
              content={<Text textType={TextType.Description} value={messages.thresholdGapError} />}
              status={BannerStatus.Error}
            />
          ) : null}
        </FlexColumn>
      ) : null}
    </FlexColumn>
  );

  return (
    <LossPreventionCard
      blockingBehavior={BLOCKING_BEHAVIOR.BLOCKS}
      description={messages.description}
      isCardDisabled={isCardDisabled}
      linkButton={
        onManageBlacklist
          ? {
              text: intl.formatMessage(messages.manageBlacklistButton),
              onClick: onManageBlacklist,
            }
          : undefined
      }
      onToggle={onFeatureToggle}
      title={messages.title}
    >
      {settings}
    </LossPreventionCard>
  );
};
